import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Calendar,
    MapPin,
    User,
    Clock,
    DollarSign,
    CheckCircle,
    AlertCircle,
    Webhook,
    RefreshCw,
    Activity,
    TrendingUp,
    Download,
    Send,
    Loader,
    HardHat,
    Building2,
    Wrench,
    FileText
} from 'lucide-react';
import { adminAPI } from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import './AppointmentTracker.scss';

const AppointmentTracker = () => {
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [webhookStatus, setWebhookStatus] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [syncMonth, setSyncMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [sendingReviewAppointmentId, setSendingReviewAppointmentId] = useState(null);
    const [filters, setFilters] = useState({
        technicianId: '',
        dateRange: '7',
        source: 'all'
    });

    useEffect(() => {
        loadData();
        loadWebhookStatus();
    }, [filters]);

    const loadData = async () => {
        try {
            setLoading(true);
            const appointmentsResponse = await adminAPI.getAppointments(filters);

            if (appointmentsResponse.success) {
                setAppointments(appointmentsResponse.data);
            } else {
                setError('Failed to load appointments');
            }
        } catch (error) {
            setError('Failed to load appointment data');
            console.error('Error loading appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadWebhookStatus = async () => {
        try {
            const statusResponse = await adminAPI.getWebhookStatus('pestpac');
            if (statusResponse.success) {
                setWebhookStatus(statusResponse.data);
            }
        } catch (error) {
            console.error('Error loading webhook status:', error);
        }
    };

    const handleSyncAppointments = async () => {
        try {
            setSyncing(true);
            setError(null);
            setMessage(null);

            const syncResponse = await adminAPI.syncAppointments({});

            if (syncResponse.success) {
                setMessage(`Successfully synced ALL appointments: ${syncResponse.data.created} new, ${syncResponse.data.updated} updated, ${syncResponse.data.totalFetched} total fetched`);
                setShowSyncModal(false);
                await loadData();
            } else {
                setError(syncResponse.message || 'Failed to sync appointments');
            }
        } catch (error) {
            setError('Failed to sync appointments');
            console.error('Error syncing appointments:', error);
        } finally {
            setSyncing(false);
        }
    };

    const handleSendReviewSolicitation = async (appointment) => {
        try {
            setSendingReviewAppointmentId(appointment.id);
            setError(null);
            setMessage(null);

            const response = await adminAPI.sendReviewSolicitationTest({
                appointmentId: Number(appointment.id)
            });

            if (response.success) {
                setMessage(response.message || `Review solicitation sent for appointment #${appointment.id}`);
            } else {
                setError(response.message || 'Failed to send review solicitation');
            }
        } catch (sendError) {
            console.error('Error sending review solicitation:', sendError);
            setError(sendError.message || 'Failed to send review solicitation');
        } finally {
            setSendingReviewAppointmentId(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return { date: 'N/A', time: '' };

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { date: 'N/A', time: '' };

        return {
            date: date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }),
            time: date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            })
        };
    };

    const formatSource = (source) => {
        switch (source) {
            case 'pestpac_sync':
            case 'pestpac_webhook':
            case 'pestpac':
                return 'PestPac';
            case 'ejento_fieldroutes':
            case 'fieldroutes':
            case 'fieldroutes_webhook':
                return 'FieldRoutes';
            case 'webhook':
                return 'Webhook';
            default:
                return source || 'Unknown';
        }
    };

    const getSourceType = (appointment) => {
        if (appointment.fieldroutesAppointmentId) {
            return 'fieldroutes';
        }
        if (appointment.pestpacAppointmentId) {
            return 'pestpac';
        }
        const source = appointment.metadata?.source || '';
        if (source.includes('fieldroutes') || source === 'ejento_fieldroutes') {
            return 'fieldroutes';
        }
        if (source.includes('pestpac') || source === 'pestpac_sync' || source === 'pestpac_webhook') {
            return 'pestpac';
        }
        return 'other';
    };

    const getSourceLabel = (appointment) => {
        const type = getSourceType(appointment);
        return type === 'fieldroutes' ? 'FIELDROUTES' : type === 'pestpac' ? 'PESTPAC' : 'OTHER';
    };

    const getStatusText = (appointment) => {
        if (appointment.metadata?.source === 'ejento_fieldroutes') {
            return appointment.metadata?.appointment?.status_text || 'Completed';
        }
        return appointment.completionStatus || 'Completed';
    };

    const filteredAppointments = appointments.filter(appointment => {
        if (filters.source === 'all') return true;
        return getSourceType(appointment) === filters.source;
    });

    if (loading) {
        return <LoadingSpinner message="Loading appointment data..." />;
    }

    return (
        <div className="appointment-tracker">
            <motion.div
                className="config-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="header-content">
                    <div className="logo-section">
                        <Calendar className="logo" />
                        <div>
                            <h1>Appointment Tracker</h1>
                            <p>Real-time appointments from webhooks - automatically updated when jobs are completed</p>
                        </div>
                    </div>

                    <div className="header-actions">
                        <button
                            className="sync-btn"
                            onClick={() => setShowSyncModal(true)}
                            disabled={syncing || loading}
                        >
                            <Download size={16} />
                            Sync Appointments
                        </button>
                        <button
                            className="refresh-btn"
                            onClick={loadData}
                            disabled={loading}
                        >
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Webhook Status Card */}
            {webhookStatus && (
                <motion.div
                    className="integration-status"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <div className="status-header">
                        <h3><Activity size={20} /> Webhook Status</h3>
                        <div className={`status-badge ${webhookStatus.isActive ? 'active' : 'inactive'}`}>
                            {webhookStatus.isActive ? (
                                <>
                                    <CheckCircle size={16} />
                                    <span>Active</span>
                                </>
                            ) : (
                                <>
                                    <AlertCircle size={16} />
                                    <span>Inactive</span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="status-grid">
                        <div className="status-item">
                            <TrendingUp className="status-icon success" />
                            <div>
                                <strong>Total Received</strong>
                                <span>{webhookStatus.totalDeliveries || 0} webhooks</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <CheckCircle className="status-icon success" />
                            <div>
                                <strong>Successful</strong>
                                <span>{webhookStatus.successfulDeliveries || 0} processed</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <AlertCircle className={`status-icon ${webhookStatus.failedDeliveries > 0 ? 'danger' : 'success'}`} />
                            <div>
                                <strong>Failed</strong>
                                <span>{webhookStatus.failedDeliveries || 0} errors</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <Clock className="status-icon" />
                            <div>
                                <strong>Last Received</strong>
                                <span>{webhookStatus.lastDelivery ? formatDate(webhookStatus.lastDelivery).date : 'Never'}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Messages */}
            {message && (
                <motion.div
                    className="message success"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                >
                    {message}
                </motion.div>
            )}

            {error && (
                <motion.div
                    className="message error"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                >
                    {error}
                </motion.div>
            )}

            {/* Appointments List */}
            <motion.div
                className="appointments-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <div className="section-header">
                    <h3><Calendar size={20} /> Recent Appointments</h3>
                    <div className="header-controls">
                        <select
                            value={filters.source}
                            onChange={(e) => setFilters({...filters, source: e.target.value})}
                            className="filter-select"
                        >
                            <option value="all">All Sources</option>
                            <option value="pestpac">PestPac</option>
                            <option value="fieldroutes">FieldRoutes</option>
                        </select>
                        <select
                            value={filters.dateRange}
                            onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                            className="filter-select"
                        >
                            <option value="1">Last 24 hours</option>
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="all">All time</option>
                        </select>
                        <span className="appointment-count">{filteredAppointments.length} of {appointments.length}</span>
                    </div>
                </div>

                {filteredAppointments.length === 0 ? (
                    <div className="empty-state">
                        <Calendar size={48} />
                        <h3>No appointments found</h3>
                        <p>
                            {filters.source !== 'all'
                                ? `No appointments from ${filters.source === 'pestpac' ? 'PestPac' : 'FieldRoutes'} found. Try selecting "All Sources".`
                                : 'Appointments will appear here automatically via webhooks when jobs are completed. Click "Refresh" above to see the latest data.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="appointments-grid">
                        {filteredAppointments.map((appointment) => {
                            const serviceDateTime = formatDate(appointment.serviceDate);
                            return (
                                <motion.div
                                    key={appointment.id}
                                    className="appointment-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Card Header */}
                                    <div className="card-header">
                                        <div className="header-left">
                                            <span className="appointment-id">
                                                #{appointment.fieldroutesAppointmentId || appointment.pestpacAppointmentId}
                                            </span>
                                            <span className="source-badge">
                                                {getSourceLabel(appointment)}
                                            </span>
                                        </div>
                                        <div className="header-right">
                                            <span className="service-date-label">SERVICE DATE</span>
                                            <span className="service-date">{serviceDateTime.date}</span>
                                            <span className="service-time">{serviceDateTime.time}</span>
                                        </div>
                                    </div>

                                    {/* Status Row */}
                                    <div className="status-row">
                                        <span className="status-label"><Activity size={12} /> STATUS</span>
                                        <span className="status-indicator completed">
                                            <span className="status-dot"></span>
                                            {getStatusText(appointment)}
                                        </span>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body">
                                        <div className="info-grid">
                                            <div className="info-column">
                                                <div className="info-row">
                                                    <span className="info-label"><User size={12} /> CUSTOMER</span>
                                                    <span className="info-value">
                                                        {appointment.customerName ||
                                                         (appointment.metadata?.customer && `${appointment.metadata.customer.fname} ${appointment.metadata.customer.lname}`) ||
                                                         `Customer #${appointment.metadata?.BillToID || appointment.pestpacCustomerId}`}
                                                        {(appointment.metadata?.customer?.city || appointment.metadata?.LocationID) && (
                                                            <span className="info-secondary">
                                                                {' • '}
                                                                {appointment.metadata?.customer?.city
                                                                    ? `${appointment.metadata.customer.city}, ${appointment.metadata.customer.state}`
                                                                    : `Location ${appointment.metadata.LocationID}`
                                                                }
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label"><HardHat size={12} /> TECHNICIAN</span>
                                                    <span className="info-value">
                                                        {appointment.technician?.name ||
                                                         appointment.metadata?.technicianName ||
                                                         appointment.metadata?.appointment?.employee_name ||
                                                         appointment.metadata?.appointment?.serviced_by_name ||
                                                         (appointment.metadata?.appointment?.serviced_by && `Tech #${appointment.metadata.appointment.serviced_by}`) ||
                                                         (appointment.metadata?.appointment?.employee_id && `Tech #${appointment.metadata.appointment.employee_id}`) ||
                                                         'Unknown Technician'}
                                                    </span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label"><Building2 size={12} /> BRANCH</span>
                                                    <span className="info-value">
                                                        {appointment.metadata?.Branch ||
                                                         appointment.metadata?.appointment?.office ||
                                                         appointment.metadata?.appointment?.office_name ||
                                                         appointment.metadata?.customer?.office ||
                                                         appointment.locationName ||
                                                         'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="service-column">
                                                <span className="service-type">
                                                    <Wrench size={14} />
                                                    {appointment.serviceType ||
                                                     appointment.metadata?.subscription?.service_type ||
                                                     appointment.metadata?.Description ||
                                                     `Service ${appointment.metadata?.ServiceCode || ''}`}
                                                </span>
                                                {(appointment.metadata?.Duration || appointment.metadata?.appointment?.duration_minutes) && (
                                                    <span className="service-duration">
                                                        <Clock size={12} /> {appointment.metadata?.Duration || appointment.metadata?.appointment?.duration_minutes} min
                                                    </span>
                                                )}
                                                {(appointment.metadata?.total || appointment.metadata?.subscription?.recurring_charge) && (
                                                    <span className="service-price">
                                                        <DollarSign size={14} />
                                                        {parseFloat(appointment.metadata?.total || appointment.metadata?.subscription?.recurring_charge).toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Service Notes */}
                                    {(appointment.serviceNotes || appointment.metadata?.appointment?.notes) && (
                                        <div className="card-notes">
                                            <span className="notes-label"><FileText size={12} /> SERVICE NOTES</span>
                                            <div className="notes-content">
                                                <p>{appointment.serviceNotes || appointment.metadata?.appointment?.notes}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="card-actions">
                                        <button
                                            className="review-trigger-btn"
                                            onClick={() => handleSendReviewSolicitation(appointment)}
                                            disabled={
                                                sendingReviewAppointmentId === appointment.id ||
                                                appointment.completionStatus !== 'completed'
                                            }
                                        >
                                            {sendingReviewAppointmentId === appointment.id ? (
                                                <>
                                                    <Loader className="spinner" size={14} />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={14} />
                                                    Send Review Solicitation
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </motion.div>

            {/* Sync Modal */}
            {showSyncModal && (
                <div className="sync-modal-overlay" onClick={() => setShowSyncModal(false)}>
                    <motion.div
                        className="sync-modal"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                    >
                        <div className="modal-header">
                            <h3>Sync Appointments</h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowSyncModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <p>Fetch appointments directly from your field service management system. This will retrieve all completed service orders and add them to your database.</p>

                            <div className="sync-info">
                                <div className="info-item">
                                    <strong>What will be synced:</strong>
                                    <ul>
                                        <li>All completed service orders</li>
                                        <li>Customer and location information</li>
                                        <li>Technician assignments</li>
                                        <li>Service details and metadata</li>
                                    </ul>
                                </div>
                            </div>

                            <p className="sync-note">
                                Existing appointments will be updated, new ones will be created.
                            </p>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowSyncModal(false)}
                                disabled={syncing}
                            >
                                Cancel
                            </button>
                            <button
                                className="sync-confirm-btn"
                                onClick={handleSyncAppointments}
                                disabled={syncing}
                            >
                                {syncing ? (
                                    <>
                                        <Loader className="spinner" size={16} />
                                        Syncing...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} />
                                        Sync Appointments
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AppointmentTracker;
